import { useCallback, useMemo } from "react";
import { useLocalStorage } from "usehooks-ts";
import type { Product, SetupProgress } from "../registry/types";

export type WidgetVisualState = "expanded" | "hidden";

/**
 * Hybrid widget state (#2):
 *   - `dismissed_at` on the progress row is TEAM-scoped — when set, the
 *     widget is gone for every user on this team. Persists in DB.
 *   - The expanded-vs-hidden UI preference is PER-DEVICE and lives in
 *     localStorage so closing on one tab doesn't nag the same user on
 *     another tab/device.
 *
 * "Close" = `setLocalState('hidden')` — only this device + this user.
 * "Dismiss for team" = caller fires the dismissMerchantSetupGuide RPC,
 * which is team-scoped and overrides per-device state.
 */
export interface WidgetStateApi {
  /** Effective visible state derived from team-dismiss + per-device pref. */
  visualState: WidgetVisualState;
  /** True when team-dismiss has been recorded — widget + header tab both gone. */
  dismissedForTeam: boolean;
  setExpanded: () => void;
  /** Per-device close. Header tab still visible so the user can re-expand. */
  closeForDevice: () => void;
}

/**
 * localStorage key for per-device widget state. Exported for unit tests.
 * Scoped by team + product + user so multi-seat / multi-team / multi-product
 * states don't bleed into each other.
 */
export function widgetStorageKey(
  teamId: string | undefined,
  product: Product,
  userId: string | undefined,
): string {
  return `setup-guide:${teamId ?? "anon"}:${product}:${userId ?? "anon"}`;
}

/**
 * Pure derivation of the effective visual state from inputs. Exported for
 * unit tests so we don't need a React render to verify the rule.
 */
export function deriveWidgetVisualState(args: {
  dismissedForTeam: boolean;
  localState: WidgetVisualState;
}): WidgetVisualState {
  if (args.dismissedForTeam) {
    return "hidden";
  }
  return args.localState;
}

export function useWidgetState(args: {
  teamId: string | undefined;
  userId: string | undefined;
  product: Product;
  /** The progress row, if any — drives the team-scoped dismiss check. */
  progress: SetupProgress | null;
}): WidgetStateApi {
  const key = widgetStorageKey(args.teamId, args.product, args.userId);
  // Default new users to 'expanded' so the widget surfaces itself once
  // intake is complete; subsequent closes/collapses persist.
  const [localState, setLocalState] = useLocalStorage<WidgetVisualState>(
    key,
    "expanded",
  );

  const dismissedForTeam = args.progress?.dismissedAt != null;

  const visualState = useMemo(
    () => deriveWidgetVisualState({ dismissedForTeam, localState }),
    [dismissedForTeam, localState],
  );

  const setExpanded = useCallback(
    () => setLocalState("expanded"),
    [setLocalState],
  );
  const closeForDevice = useCallback(
    () => setLocalState("hidden"),
    [setLocalState],
  );

  return { visualState, dismissedForTeam, setExpanded, closeForDevice };
}
